import React, { useState, useRef, useEffect } from "react";
import "./Menu.css";

// Custom hook to detect clicks outside the dropdown
const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
};

const Menu = ({ mainMenuCategories, fashionCategories, moreCategories, setSelectedCategory }) => {
  const [fashionMenuOpen, setFashionMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768); // For determining if it's large screen

  const sidebarRef = useRef(null); // Reference to the sidebar
  const fashionDropdownRef = useRef(null);
  const moreDropdownRef = useRef(null);

  useClickOutside(fashionDropdownRef, () => setFashionMenuOpen(false));
  useClickOutside(moreDropdownRef, () => setMoreMenuOpen(false));
  useClickOutside(sidebarRef, () => setSidebarOpen(false)); // Close the sidebar if clicked outside

  // Listen to window resize to check if we're in large screen mode
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const closeSidebar = () => {
    setSidebarOpen(false);
    setFashionMenuOpen(false); // Close fashion submenu when sidebar is closed
    setMoreMenuOpen(false); // Close more submenu when sidebar is closed
  };

  const toggleFashionMenu = () => {
    if (isLargeScreen) {
      // Only toggle submenu for large screens from the main menu
      setFashionMenuOpen(!fashionMenuOpen);
      setMoreMenuOpen(false); // Close "More" menu when opening "Fashion" menu
    } else {
      // Handle submenu in hamburger menu for small screens
      setFashionMenuOpen(!fashionMenuOpen);
      setMoreMenuOpen(false);
    }
  };

  const toggleMoreMenu = () => {
    if (isLargeScreen) {
      // Only toggle submenu for large screens from the main menu
      setMoreMenuOpen(!moreMenuOpen);
      setFashionMenuOpen(false); // Close "Fashion" menu when opening "More" menu
    } else {
      // Handle submenu in hamburger menu for small screens
      setMoreMenuOpen(!moreMenuOpen);
      setFashionMenuOpen(false);
    }
  };

  // Function to handle submenu item click
  const handleSubmenuItemClick = (category) => {
    setSelectedCategory(category.searchIndex);
    if (!isLargeScreen) {
      closeSidebar(); // Close sidebar for small screens
    } else {
      setFashionMenuOpen(false); // Close fashion submenu for large screens
      setMoreMenuOpen(false); // Close more submenu for large screens
    }
  };

  return (
    <>
      <div className={`hamburger ${sidebarOpen ? "hide" : ""}`} onClick={() => setSidebarOpen(true)}>
        ☰
      </div>

      <nav className="menu">
        {mainMenuCategories.map((category) => (
          <a key={category.searchIndex} href="#" className="menu-item" onClick={() => setSelectedCategory(category.searchIndex)}>
            {category.displayName}
          </a>
        ))}

        {/* Main Menu Dropdowns (Only show on large screen) */}
        {isLargeScreen && (
          <>
            <div className="dropdown">
              <button className="dropdown-button" onClick={toggleFashionMenu}>
                Fashion
              </button>
              <div className={`dropdown-content ${fashionMenuOpen ? "show" : ""}`} ref={fashionDropdownRef}>
                {fashionCategories.map((category) => (
                  <a
                    key={category.searchIndex}
                    href="#"
                    className="dropdown-item"
                    onClick={() => handleSubmenuItemClick(category)}
                  >
                    {category.displayName}
                  </a>
                ))}
              </div>
            </div>

            <div className="dropdown">
              <button className="dropdown-button" onClick={toggleMoreMenu}>
                More
              </button>
              <div className={`dropdown-content ${moreMenuOpen ? "show" : ""}`} ref={moreDropdownRef}>
                {moreCategories.map((category) => (
                  <a
                    key={category.searchIndex}
                    href="#"
                    className="dropdown-item"
                    onClick={() => handleSubmenuItemClick(category)}
                  >
                    {category.displayName}
                  </a>
                ))}
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Sidebar Menu for Mobile */}
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`} ref={sidebarRef}>
        <span className="close-btn" onClick={closeSidebar}>✖</span>

        {mainMenuCategories.map((category) => (
          <a
            key={category.searchIndex}
            href="#"
            className="menu-item"
            onClick={() => {
              setSelectedCategory(category.searchIndex);
              closeSidebar();
            }}
          >
            {category.displayName}
          </a>
        ))}

        {/* Only show dropdowns for mobile (hamburger) */}
        {!isLargeScreen && (
          <>
            <button className="dropdown-button" onClick={toggleFashionMenu}>
              Fashion
            </button>
            <div className={`dropdown-content ${fashionMenuOpen ? "show" : ""}`} ref={fashionDropdownRef}>
              {fashionCategories.map((category) => (
                <a
                  key={category.searchIndex}
                  href="#"
                  className="dropdown-item"
                  onClick={() => handleSubmenuItemClick(category)}
                >
                  {category.displayName}
                </a>
              ))}
            </div>

            <button className="dropdown-button" onClick={toggleMoreMenu}>
              More
            </button>
            <div className={`dropdown-content ${moreMenuOpen ? "show" : ""}`} ref={moreDropdownRef}>
              {moreCategories.map((category) => (
                <a
                  key={category.searchIndex}
                  href="#"
                  className="dropdown-item"
                  onClick={() => handleSubmenuItemClick(category)}
                >
                  {category.displayName}
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Menu;